import type { UseFormReturnType } from "@mantine/form";
import { Alert, Button, Collapse, Stack, UnstyledButton } from "@mantine/core";
import { IconAlertCircle, IconArrowRight, IconChevronDown } from "@tabler/icons-react";
import { useState } from "react";
import type { ReactNode } from "react";
import type { FormEvent } from "react";
import type { LoginFormValues } from "../useLoginForm";
import type { LoginTr } from "../useLoginTranslations";
import type { LoginPageFooterLink } from "../LoginPage";
import styles from "../LoginPage.module.css";
import { AuthEyebrowDivider, AuthSplitLayout } from "./AuthSplitLayout";
import { EmailPasswordFields } from "./LoginForm/EmailPasswordFields";
import { LoginFormFooter, SignInSignUpToggle } from "./LoginForm/LoginFormFooter";
import { SocialSignInButtons } from "./LoginForm/SocialSignInButtons";

/**
 * SSO is currently used by a small number of customers, so it is hidden behind a
 * disclosure toggle by default and email/password is the primary path. When SSO is
 * rolled out more broadly, flip this to `true` (or pass `ssoDefaultExpanded`) to show
 * the providers by default.
 */
const SSO_EXPANDED_BY_DEFAULT = false;

export interface LoginFormViewProps {
  form: UseFormReturnType<LoginFormValues>;
  isSignUp: boolean;
  onToggleSignUp: () => void;
  onForgotPasswordClick: () => void;
  error: string;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onGoogleSignIn: () => void;
  onMicrosoftSignIn: () => void;
  authLoading: boolean;
  socialLoading: "google" | "microsoft" | null;
  title?: ReactNode;
  subtitle?: ReactNode;
  logo?: ReactNode;
  footerAgreement?: ReactNode;
  footerSupportLink?: LoginPageFooterLink;
  emailVerifiedFromLink?: boolean;
  emailVerifiedFromLinkMessage?: string;
  /** Show the SSO providers expanded by default instead of behind the disclosure toggle. */
  ssoDefaultExpanded?: boolean;
  tr: LoginTr;
}

export function LoginFormView({
  form,
  isSignUp,
  onToggleSignUp,
  onForgotPasswordClick,
  error,
  onSubmit,
  onGoogleSignIn,
  onMicrosoftSignIn,
  authLoading,
  socialLoading,
  title,
  subtitle,
  logo,
  footerAgreement,
  footerSupportLink,
  emailVerifiedFromLink,
  emailVerifiedFromLinkMessage,
  ssoDefaultExpanded = SSO_EXPANDED_BY_DEFAULT,
  tr,
}: LoginFormViewProps) {
  // Default title/subtitle show unless the host explicitly opts out with `null`.
  // (`undefined` / not passed => use the default; `null` => hide.)
  const defaultTitle = isSignUp
    ? tr("login.form.signUpTitle", "Create your account")
    : tr("login.form.signInTitle", "Welcome back");
  const resolvedTitle = title === undefined ? defaultTitle : (title ?? undefined);

  const defaultSubtitle = isSignUp
    ? undefined
    : tr("login.form.subtitleSignIn", "Log in to your Collective Discovery account.");
  const resolvedSubtitle = subtitle === undefined ? defaultSubtitle : (subtitle ?? undefined);

  const [ssoExpanded, setSsoExpanded] = useState(ssoDefaultExpanded);

  return (
    <AuthSplitLayout
      brandMode={isSignUp ? "signup" : "login"}
      logo={logo}
      tr={tr}
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
    >
      {emailVerifiedFromLink ? (
        <Alert color="green" title={tr("login.emailConfirmed.title", "Email confirmed")} mb="md" radius="md">
          {emailVerifiedFromLinkMessage}
        </Alert>
      ) : null}

      <form onSubmit={onSubmit}>
        <Stack gap="sm">
          <EmailPasswordFields
            emailInputProps={form.getInputProps("email")}
            passwordInputProps={form.getInputProps("password")}
            confirmPasswordInputProps={form.getInputProps("confirmPassword")}
            isSignUp={isSignUp}
            onForgotPasswordClick={onForgotPasswordClick}
            tr={tr}
          />

          {error ? (
            <Alert icon={<IconAlertCircle size={16} />} title={tr("login.errorTitle", "Error")} color="red" radius="md">
              {error}
            </Alert>
          ) : null}

          <Button
            type="submit"
            fullWidth
            size="md"
            color="secondary"
            className={styles.ctaButton}
            rightSection={<IconArrowRight size={18} />}
            loading={authLoading}
            disabled={authLoading}
          >
            {isSignUp ? tr("login.form.createAccount", "Create Account") : tr("login.form.signIn", "Sign In")}
          </Button>

          <div className={styles.ssoSection}>
            {ssoExpanded ? (
              <AuthEyebrowDivider label={tr("login.dividerContinueWith", "Or continue with")} />
            ) : (
              <UnstyledButton
                type="button"
                className={styles.ssoToggle}
                onClick={() => setSsoExpanded(true)}
                aria-expanded={false}
              >
                {tr("login.otherSignInOptions", "Other sign-in options")}
                <IconChevronDown size={15} />
              </UnstyledButton>
            )}
            <Collapse in={ssoExpanded}>
              <div className={styles.ssoButtons}>
                <SocialSignInButtons
                  onGoogle={onGoogleSignIn}
                  onMicrosoft={onMicrosoftSignIn}
                  socialLoading={socialLoading}
                  layout="row"
                  tr={tr}
                />
              </div>
            </Collapse>
          </div>

          <SignInSignUpToggle isSignUp={isSignUp} onToggle={onToggleSignUp} tr={tr} />

          <LoginFormFooter footerAgreement={footerAgreement} footerSupportLink={footerSupportLink} />
        </Stack>
      </form>
    </AuthSplitLayout>
  );
}
